import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Zap, TrendingDown, Leaf, Home, Shield, Settings } from "lucide-react";

const SolarInfo = () => {
  const benefits = [
    {
      icon: TrendingDown,
      title: "Reduce Energy Bills",
      description: "Cut your electricity costs by up to 90% with our high-efficiency solar panels."
    },
    {
      icon: Leaf,
      title: "Environmental Impact",
      description: "Reduce your carbon footprint and contribute to a cleaner, greener planet."
    },
    {
      icon: Home,
      title: "Increase Property Value",
      description: "Solar installations can increase your home's value by an average of 4%."
    },
    {
      icon: Shield,
      title: "Energy Independence",
      description: "Generate your own clean energy and reduce dependence on the grid."
    },
    {
      icon: Zap,
      title: "Reliable Technology",
      description: "Modern solar panels are built to last 25+ years with minimal maintenance."
    },
    {
      icon: Settings,
      title: "Smart Monitoring",
      description: "Track your energy production and consumption with advanced monitoring systems."
    }
  ];

  const facts = [
    { stat: "25+", label: "Years Lifespan" },
    { stat: "90%", label: "Energy Bill Reduction" },
    { stat: "4%", label: "Home Value Increase" },
    { stat: "20+", label: "Years Warranty" }
  ];

  const steps = [
    {
      step: "1",
      title: "Free Consultation",
      description: "We assess your property and energy needs to design the perfect solar solution."
    },
    {
      step: "2", 
      title: "Custom Design",
      description: "Our engineers create a tailored solar system optimized for your specific requirements."
    },
    {
      step: "3",
      title: "Professional Installation", 
      description: "Our certified technicians install your system quickly and safely with minimal disruption."
    },
    {
      step: "4",
      title: "Monitoring & Support",
      description: "Enjoy ongoing monitoring, maintenance, and support throughout your system's lifetime."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-solar-light-blue to-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 border-primary text-primary">
            Solar Energy Benefits
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-solar bg-clip-text text-transparent">
            Why Choose Solar Energy?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Solar energy is the future of sustainable power. Discover the many benefits of making the switch to clean, renewable energy.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, index) => (
            <Card key={index} className="border-0 shadow-card-solar hover:shadow-solar transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gradient-solar rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-primary">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mb-20">
          <h3 className="text-3xl font-bold text-center mb-12 text-foreground">
            Solar Panel Facts
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {facts.map((fact, index) => (
              <Card key={index} className="border-0 shadow-card-solar bg-gradient-to-br from-background to-solar-light-blue">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">{fact.stat}</div>
                  <p className="text-muted-foreground">{fact.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-3xl font-bold text-center mb-12 text-foreground">
            Our Installation Process
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <Card key={index} className="border-0 shadow-card-solar hover:shadow-solar transition-all duration-300 relative">
                <CardContent className="p-6 text-center">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="w-8 h-8 bg-gradient-energy rounded-full flex items-center justify-center">
                      <span className="text-primary-foreground font-bold text-sm">{step.step}</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h4 className="text-xl font-bold mb-3 text-primary">{step.title}</h4>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolarInfo;